import React, { useState } from 'react';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { Button, Input } from '../components/UI';
import { AlertCircle, UserPlus, LogIn, ShieldCheck, Trophy, Radio, Users } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';

export const Login: React.FC = () => {
  const { login, error, isLoading } = useAuth();
  const { settings } = useLeague();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await login(username, password);
      navigate('/');
    } catch {
      // AuthContext owns the visible error state.
    }
  };

  const getLogoUrl = () => {
    if (!settings?.league_logo_url) return null;
    if (settings.league_logo_url.startsWith('http') || settings.league_logo_url.startsWith('data:')) return settings.league_logo_url;
    const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
    return baseUrl ? `${baseUrl}/${settings.league_logo_url}` : settings.league_logo_url;
  };

  const logoUrl = getLogoUrl();
  const leagueName = settings?.league_name || 'Sunday Driver Cup';

  return (
    <div className="sdc-login-grid min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="relative z-10 w-full max-w-6xl animate-fade-in">
        <div className="grid overflow-hidden rounded-[1.75rem] border border-white/[0.08] bg-[#0b0d14]/80 shadow-[0_35px_120px_rgba(0,0,0,0.55)] backdrop-blur-xl lg:grid-cols-[1.12fr_0.88fr]">
          <section className="relative hidden min-h-[660px] flex-col justify-between overflow-hidden border-r border-white/[0.06] p-10 lg:flex xl:p-14">
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-f1-red via-f1-red/40 to-transparent" />
            <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full border border-f1-red/20 shadow-[0_0_0_55px_rgba(255,255,255,0.012),0_0_0_110px_rgba(255,255,255,0.008)]" />
            <div className="relative flex items-center gap-4">
              {logoUrl ? (
                <div className="flex h-16 min-w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] p-2.5">
                  <img src={logoUrl} alt={`${leagueName} Logo`} className="max-h-11 max-w-[9rem] object-contain" />
                </div>
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-f1-red text-xl font-bold italic text-white shadow-glow">
                  {leagueName.substring(0, 3).toUpperCase()}
                </div>
              )}
              <div><p className="sdc-eyebrow">League Control Centre</p><p className="mt-1 text-sm font-semibold text-white/65">{leagueName}</p></div>
            </div>
            <div className="relative max-w-xl">
              <p className="sdc-eyebrow mb-5">Racing. Regulated. Together.</p>
              <h1 className="sdc-display text-[clamp(3.5rem,6vw,6.5rem)] text-white">Deine Liga.<br />Ein Cockpit.</h1>
              <p className="mt-7 max-w-lg text-base leading-7 text-white/45">Rennkalender, Wertungen, Rennleitung und Community – zentral organisiert für faire und spannende Sonntage.</p>
            </div>
            <div className="relative grid grid-cols-3 gap-3">
              {[{ icon: Trophy, label: 'Wertungen' }, { icon: Radio, label: 'Race Control' }, { icon: Users, label: 'Community' }].map(({ icon: Icon, label }) => (
                <div key={label} className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
                  <Icon className="mb-3 h-4 w-4 text-f1-red" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-white/55">{label}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="sdc-login-card flex min-h-[620px] flex-col justify-center p-6 sm:p-10 lg:min-h-[660px] lg:rounded-none lg:border-0 lg:bg-transparent xl:p-14">
            <div className="mx-auto w-full max-w-md">
              <div className="mb-9 lg:hidden">
                <div className="mb-5 flex items-center gap-3">
                  {logoUrl ? <img src={logoUrl} alt={`${leagueName} Logo`} className="max-h-14 max-w-[11rem] object-contain" /> : <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-f1-red text-sm font-bold italic text-white">{leagueName.substring(0, 3).toUpperCase()}</div>}
                </div>
                <p className="sdc-eyebrow">League Control Centre</p>
              </div>
              <div className="mb-8">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-f1-red/20 bg-f1-red/10 text-f1-red"><ShieldCheck size={20} /></div>
                <h2 className="text-3xl font-bold tracking-tight text-white">Willkommen zurück</h2>
                <p className="mt-2 text-sm text-white/40">Melde dich an, um dein Liga-Dashboard zu öffnen.</p>
              </div>
              {error && <div role="alert" className="mb-5 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3.5 text-sm text-red-100"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" /><span>{error}</span></div>}
              <form onSubmit={handleSubmit}>
                <Input label="Benutzername" value={username} onChange={event => setUsername(event.target.value)} placeholder="Dein Fahrername" autoComplete="username" autoFocus required />
                <Input label="Passwort" type="password" value={password} onChange={event => setPassword(event.target.value)} placeholder="Dein Passwort" autoComplete="current-password" required />
                <Button className="mt-2 w-full py-3" disabled={isLoading} icon={LogIn}>{isLoading ? 'Anmeldung läuft …' : 'Anmelden'}</Button>
              </form>
              <div className="my-7 flex items-center gap-4"><div className="h-px flex-1 bg-white/[0.07]" /><span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/20">Neu in der Liga?</span><div className="h-px flex-1 bg-white/[0.07]" /></div>
              <Link to="/register" className="block"><Button variant="secondary" className="w-full" icon={UserPlus}>Fahrer-Account erstellen</Button></Link>
              <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-white/20"><ShieldCheck size={12} /><span>Geschützter Ligabereich · Sunday Driver Cup</span></div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
